export type RetryAnchor = 'SENT_DATE' | 'DUE_DATE'

export const RETRY_ANCHOR_OPTIONS: { value: RetryAnchor; label: string }[] = [
  { value: 'DUE_DATE', label: 'Due date' },
  { value: 'SENT_DATE', label: 'Sent date' }
]

export const DEFAULT_RETRY_ANCHOR: RetryAnchor = 'DUE_DATE'

// A retry schedule is "retry every INTERVAL days, up to MAX_RETRIES times". The backend accepts up
// to 20 offsets, each 0–365 days out and at least two days apart (in-flight/settling payments are
// skipped when a retry fires, so retries don't need wider spacing to stay safe).
export const MIN_INTERVAL_DAYS = 2
export const MAX_OFFSET_DAYS = 365
export const MAX_RETRIES = 20

export const DEFAULT_INTERVAL_DAYS = 7
export const DEFAULT_MAX_RETRIES = 10

// Interval choices offered in the dropdown. All are at least two days, so the schedule can never be
// set closer than the backend allows.
export const INTERVAL_OPTIONS: { value: string; label: string }[] = [
  2, 3, 5, 7, 10, 14, 21, 30
].map(days => ({ value: String(days), label: `${days} days` }))

export const sortOffsets = (offsets: number[]): number[] =>
  [...offsets].sort((first, second) => first - second)

export const clampInterval = (interval: number): number =>
  Math.min(Math.max(Math.trunc(interval), MIN_INTERVAL_DAYS), MAX_OFFSET_DAYS)

export const clampMaxRetries = (maxRetries: number): number =>
  Math.min(Math.max(Math.trunc(maxRetries), 1), MAX_RETRIES)

// Parse an in-progress field to a whole number, or null when empty/non-numeric.
export const toWholeNumber = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const value = Number(trimmed)
  if (Number.isNaN(value)) return null
  return Math.trunc(value)
}

// Validation message for the retries field; null when in range (empty falls back to the saved value).
export const maxRetriesError = (maxRetries: number | null): string | null => {
  if (maxRetries === null) return null
  if (maxRetries < 1) return 'Schedule at least 1 retry'
  if (maxRetries > MAX_RETRIES)
    return `You can schedule up to ${MAX_RETRIES} retries`
  return null
}

// Expand "every INTERVAL days, up to MAX_RETRIES" into the day offsets the backend stores, capped so
// the furthest retry stays within a year: e.g. every 7 up to 10 -> [7, 14, … 70].
export const buildScheduleOffsets = (
  interval: number,
  maxRetries: number
): number[] => {
  const days = clampInterval(interval)
  const withinYear = Math.floor(MAX_OFFSET_DAYS / days)
  const count = Math.min(clampMaxRetries(maxRetries), withinYear)
  return Array.from({ length: count }, (_, index) => (index + 1) * days)
}

// A stored schedule is a uniform cadence, so the interval is the first offset and the max is how many
// there are.
export const intervalFromOffsets = (offsets: number[]): number =>
  offsets.length === 0 ? DEFAULT_INTERVAL_DAYS : clampInterval(offsets[0])

export const maxRetriesFromOffsets = (offsets: number[]): number =>
  offsets.length === 0 ? DEFAULT_MAX_RETRIES : offsets.length

export const DEFAULT_RETRY_OFFSETS = buildScheduleOffsets(
  DEFAULT_INTERVAL_DAYS,
  DEFAULT_MAX_RETRIES
)
